import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@/assets/css/reset.less';
import '@/assets/css/common.less';
import '@/assets/css/design-system.less';
import '@/i18n';
import Router from '@/router/index';
import '@ant-design/v5-patch-for-react-19';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';

import axios from 'axios';
import { OpenAPI } from '@/apis';
import { normalizeImageUrlsInValue } from '@/utils/normalizeImageUrl';
axios.defaults.withCredentials = true;
// 配置 OpenAPI 基础地址与 Cookie 跨域
if (import.meta.env.PROD) {
  OpenAPI.BASE = '//api.jinanyingchu.com';
} else {
  // 开发环境走本地代理
  OpenAPI.BASE = '';
}
OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'include';

// 从 localStorage 加载 token
const savedToken = localStorage.getItem('token');
if (savedToken) {
  OpenAPI.TOKEN = savedToken;
}

// 添加请求拦截器，自动添加 token
axios.interceptors.request.use(
  function (config) {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// 添加响应拦截器
axios.interceptors.response.use(
  function (response) {
    // 开发环境：将接口返回中 host 为 localhost:3030 的图片地址转为相对路径，走 Vite 代理
    if (import.meta.env.DEV && response.data != null) {
      response.data = normalizeImageUrlsInValue(
        response.data
      ) as typeof response.data;
    }
    if (response.data.errNo === 0) {
      return response;
    } else {
      return Promise.reject(response.data.errstr);
    }
  },
  function (error) {
    if (error.response) {
      if (error.response.status == '401') {
        window.location.replace(`/login`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log(error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }
    return Promise.reject(error);
  }
);
const themeConfig = {
  token: {
    colorPrimary: '#6366f1',
    colorPrimaryHover: '#4f46e5',
    colorPrimaryActive: '#4338ca',
    colorSuccess: '#10b981',
    colorWarning: '#f59e0b',
    colorError: '#ef4444',
    colorInfo: '#06b6d4',
    colorBgContainer: '#ffffff',
    colorBgBase: '#f8fafc',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f1f5f9',
    colorText: '#1e293b',
    colorTextSecondary: '#64748b',
    colorTextTertiary: '#94a3b8',
    colorTextQuaternary: '#cbd5e1',
    colorBorder: '#e2e8f0',
    colorBorderSecondary: '#f1f5f9',
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    boxShadowSecondary:
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
  },
  components: {
    Button: {
      borderRadius: 8,
      fontWeight: 500,
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    },
    Card: {
      borderRadius: 12,
      boxShadow:
        '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    },
    Input: {
      borderRadius: 8,
      colorBorder: '#e2e8f0',
      colorPrimaryBorder: '#6366f1',
    },
    Menu: {
      borderRadius: 8,
      colorItemBg: 'transparent',
      colorItemBgHover: '#f1f5f9',
      colorItemBgSelected: '#6366f1',
      colorItemTextSelected: '#ffffff',
    },
    Table: {
      borderRadius: 8,
      headerBg: '#f8fafc',
      headerColor: '#1e293b',
      rowHoverBg: '#f8fafc',
    },
  },
};

function AppWithLocale() {
  const { i18n } = useTranslation();
  const antdLocale = i18n.language === 'en' ? enUS : zhCN;
  return (
    <ConfigProvider locale={antdLocale} theme={themeConfig}>
      <Router />
    </ConfigProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppWithLocale />
  </StrictMode>
);
